import { rankOf, suitOf, type Card } from './cards';

/**
 * Poker hand evaluation shared by Video Poker (5 cards) and Three Card
 * Poker (3 cards). Card values run 2..14 with the ace high (it also plays
 * low in A-2-3-4-5 and A-2-3 straights).
 */
export function pokerValue(card: Card): number {
  const r = rankOf(card);
  return r === 0 ? 14 : r + 1;
}

function isFlush(cards: Card[]): boolean {
  return cards.every((c) => suitOf(c) === suitOf(cards[0]!));
}

/** High card of a straight, or 0 if the values aren't one. */
function straightHigh(values: number[]): number {
  const sorted = [...new Set(values)].sort((a, b) => a - b);
  if (sorted.length !== values.length) return 0;
  const n = values.length;
  if (sorted[n - 1]! - sorted[0]! === n - 1) return sorted[n - 1]!;
  // Ace low: A-2-3(-4-5).
  const wheel = [...Array(n - 1).keys()].map((i) => i + 2).concat(14);
  if (wheel.every((v, i) => sorted[i] === v)) return n;
  return 0;
}

function countsByValue(values: number[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return counts;
}

// ── Five cards (Video Poker) ──

export type FiveCardHand =
  | 'royalFlush'
  | 'straightFlush'
  | 'fourOfAKind'
  | 'fullHouse'
  | 'flush'
  | 'straight'
  | 'threeOfAKind'
  | 'twoPair'
  | 'jacksOrBetter'
  | 'nothing';

export const FIVE_CARD_NAMES: Record<FiveCardHand, string> = {
  royalFlush: 'Royal Flush',
  straightFlush: 'Straight Flush',
  fourOfAKind: 'Four of a Kind',
  fullHouse: 'Full House',
  flush: 'Flush',
  straight: 'Straight',
  threeOfAKind: 'Three of a Kind',
  twoPair: 'Two Pair',
  jacksOrBetter: 'Jacks or Better',
  nothing: 'Nothing',
};

export function evaluateFive(cards: Card[]): FiveCardHand {
  const values = cards.map(pokerValue);
  const flush = isFlush(cards);
  const high = straightHigh(values);
  if (flush && high === 14 && values.includes(13)) return 'royalFlush';
  if (flush && high) return 'straightFlush';
  const groups = [...countsByValue(values).entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  const [first, second] = groups;
  if (first![1] === 4) return 'fourOfAKind';
  if (first![1] === 3 && second?.[1] === 2) return 'fullHouse';
  if (flush) return 'flush';
  if (high) return 'straight';
  if (first![1] === 3) return 'threeOfAKind';
  if (first![1] === 2 && second?.[1] === 2) return 'twoPair';
  if (first![1] === 2 && first![0] >= 11) return 'jacksOrBetter';
  return 'nothing';
}

// ── Three cards (Three Card Poker) ──

/** Higher is better. In three-card poker a straight beats a flush. */
export enum ThreeCardRank {
  HighCard = 1,
  Pair = 2,
  Flush = 3,
  Straight = 4,
  ThreeOfAKind = 5,
  StraightFlush = 6,
}

export const THREE_CARD_NAMES: Record<ThreeCardRank, string> = {
  [ThreeCardRank.HighCard]: 'High card',
  [ThreeCardRank.Pair]: 'Pair',
  [ThreeCardRank.Flush]: 'Flush',
  [ThreeCardRank.Straight]: 'Straight',
  [ThreeCardRank.ThreeOfAKind]: 'Three of a Kind',
  [ThreeCardRank.StraightFlush]: 'Straight Flush',
};

/** [rank, ...tiebreak values], compared left to right. */
export function scoreThree(cards: Card[]): number[] {
  const values = cards.map(pokerValue).sort((a, b) => b - a);
  const flush = isFlush(cards);
  const high = straightHigh(values);
  if (flush && high) return [ThreeCardRank.StraightFlush, high];
  if (values[0] === values[2]) return [ThreeCardRank.ThreeOfAKind, values[0]!];
  if (high) return [ThreeCardRank.Straight, high];
  if (flush) return [ThreeCardRank.Flush, ...values];
  if (values[0] === values[1] || values[1] === values[2]) {
    const pair = values[1]!;
    const kicker = values[0] === pair ? values[2]! : values[0]!;
    return [ThreeCardRank.Pair, pair, kicker];
  }
  return [ThreeCardRank.HighCard, ...values];
}

export function compareScores(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

// ── Seven cards (Texas Hold'em: best five of seven) ──

/** Higher is better. */
export enum HoldemRank {
  HighCard = 0,
  Pair = 1,
  TwoPair = 2,
  Trips = 3,
  Straight = 4,
  Flush = 5,
  FullHouse = 6,
  Quads = 7,
  StraightFlush = 8,
}

/** High card of the best 5-long run in a set of values (ace also low), or 0. */
function runHigh(present: readonly boolean[]): number {
  let run = 0;
  for (let v = 14; v >= 1; v--) {
    // Index 1 stands for the ace playing low.
    run = present[v === 1 ? 14 : v] ? run + 1 : 0;
    if (run === 5) return v + 4;
  }
  return 0;
}

/**
 * The best five-card hand in 5-7 cards, as [rank, ...tiebreak values],
 * compared left to right with compareScores. Built for speed: the AIs and
 * the win-chance hint run it thousands of times a decision.
 */
export function scoreSeven(cards: readonly Card[]): number[] {
  const counts = new Array<number>(15).fill(0);
  const suitCounts = [0, 0, 0, 0];
  for (const c of cards) {
    counts[pokerValue(c)]!++;
    suitCounts[suitOf(c)]!++;
  }
  const flushSuit = suitCounts.findIndex((n) => n >= 5);
  if (flushSuit >= 0) {
    const inSuit = new Array<boolean>(15).fill(false);
    for (const c of cards) if (suitOf(c) === flushSuit) inSuit[pokerValue(c)] = true;
    const sf = runHigh(inSuit);
    if (sf) return [HoldemRank.StraightFlush, sf];
  }
  const quads: number[] = [];
  const trips: number[] = [];
  const pairs: number[] = [];
  const singles: number[] = [];
  for (let v = 14; v >= 2; v--) {
    const n = counts[v]!;
    if (n === 4) quads.push(v);
    else if (n === 3) trips.push(v);
    else if (n === 2) pairs.push(v);
    else if (n === 1) singles.push(v);
  }
  // Kickers: the highest values left, whatever group they came from.
  const kickers = (skip: number[], k: number) => {
    const out: number[] = [];
    for (let v = 14; v >= 2 && out.length < k; v--) if (counts[v]! > 0 && !skip.includes(v)) out.push(v);
    return out;
  };
  if (quads.length) return [HoldemRank.Quads, quads[0]!, ...kickers([quads[0]!], 1)];
  if (trips.length && (trips.length > 1 || pairs.length)) {
    const pair = Math.max(trips[1] ?? 0, pairs[0] ?? 0);
    return [HoldemRank.FullHouse, trips[0]!, pair];
  }
  if (flushSuit >= 0) {
    const vals = cards.filter((c) => suitOf(c) === flushSuit).map(pokerValue).sort((a, b) => b - a).slice(0, 5);
    return [HoldemRank.Flush, ...vals];
  }
  const straight = runHigh(counts.map((n) => n > 0));
  if (straight) return [HoldemRank.Straight, straight];
  if (trips.length) return [HoldemRank.Trips, trips[0]!, ...kickers([trips[0]!], 2)];
  if (pairs.length >= 2) return [HoldemRank.TwoPair, pairs[0]!, pairs[1]!, ...kickers([pairs[0]!, pairs[1]!], 1)];
  if (pairs.length) return [HoldemRank.Pair, pairs[0]!, ...kickers([pairs[0]!], 3)];
  return [HoldemRank.HighCard, ...kickers([], 5)];
}

const VALUE_NAMES = ['', '', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

/** A short name for a scoreSeven result ("Pair of Ks", "Flush"). */
export function holdemHandName(score: readonly number[]): string {
  const v = (i: number) => `${VALUE_NAMES[score[i]!]}s`;
  switch (score[0]) {
    case HoldemRank.StraightFlush:
      return score[1] === 14 ? 'Royal flush' : 'Straight flush';
    case HoldemRank.Quads:
      return `Four ${v(1)}`;
    case HoldemRank.FullHouse:
      return `${v(1)} full`;
    case HoldemRank.Flush:
      return 'Flush';
    case HoldemRank.Straight:
      return 'Straight';
    case HoldemRank.Trips:
      return `Three ${v(1)}`;
    case HoldemRank.TwoPair:
      return `${v(1)} and ${v(2)}`;
    case HoldemRank.Pair:
      return `Pair of ${v(1)}`;
    default:
      return `${VALUE_NAMES[score[1]!]} high`;
  }
}
